// 微专栏数据看板埋点（v5 方案）
// 文档：docs/microcolumn/2026-05-10-dashboard-tracking-final.md
//
// 设计要点：
// 1. 上报全部走裸 fetch，不经过业务 request wrapper，避免 401 → /login 跳转、
//    412 → /bind 跳转以及 Arco Message.warning 弹窗污染。
// 2. 后端 best-effort：失败也回 code:0，客户端永远不要抛错弹窗。
// 3. 设备唯一标识 did 存 localStorage，与微信 OAuth 解耦；用户未登录也能上报。
// 4. sessionStorage 节流（2026-06-10 PM 调整）：同一 (post, viewType) 在同一自然日
//    （北京时间）内只发一次；与服务端反爬 1 次/天完全对齐。原 5 分钟时间戳已淘汰。
//    刷新 Tab 即重置（与 App 端 in-memory yyyymmdd 节流口径基本一致）。
// 5. inboundChannel 从 URL ?from=xxx 提取；分享链接由生成方注入 ?from=<channel>。
//
// 字面量常量集中在 ../constants/tracker（与 App/H5/backend 跨端对齐），详见该文件头注释。

import {
  TRACK_API,
  CLIENT as TRACK_CLIENT,
  DID_STORAGE_KEY,
  TRACK_THROTTLE_PREFIX as TRACK_PREFIX,
  currentDateKey,
  type ViewType as TrackerViewType,
} from "../constants/tracker";

export type ViewType = TrackerViewType;

/**
 * 设备唯一标识：localStorage UUID。
 * crypto.randomUUID 在 HTTPS / 现代浏览器可用，降级用时间戳 + 随机串。
 */
function getDid(): string {
  try {
    let did = localStorage.getItem(DID_STORAGE_KEY);
    if (!did) {
      did =
        typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
          ? crypto.randomUUID()
          : `web-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      localStorage.setItem(DID_STORAGE_KEY, did);
    }
    return did;
  } catch {
    // localStorage 不可用（隐私模式 / SSR），用进程内 fallback，
    // 不持久化也好过空字符串。
    return `web-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}

function shouldTrack(post: string, viewType: ViewType): boolean {
  try {
    const key = `${TRACK_PREFIX}${post}:${viewType}`;
    // 2026-06-10：按自然日键（yyyymmdd）去重；缓存命中今日即跳过。
    // 历史时间戳格式（5 分钟节流时代写入的数字字符串）天然不等于 yyyymmdd，
    // 自动视为"未上报过今日"，会重新发一次并由服务端反爬挡住，符合预期。
    const cached = sessionStorage.getItem(key);
    return cached !== currentDateKey();
  } catch {
    // sessionStorage 不可用 → 不节流，让请求过
    return true;
  }
}

function markTracked(post: string, viewType: ViewType): void {
  try {
    const key = `${TRACK_PREFIX}${post}:${viewType}`;
    sessionStorage.setItem(key, currentDateKey());
  } catch {
    // ignore
  }
}

/**
 * 从当前 URL ?from=xxx 提取入站渠道。
 *
 * 2026-05-15 W-2 修正：fallback 默认从 'direct' 改为空字符串 ""，
 * 让"无渠道标记"通过空字符串语义透传给 trackView，由 trackView 决定
 * 是否不写 inboundChannel 字段（自然流量归 'natural' 桶）。
 * 调用方仍可显式传 fallback（如 'direct'）覆盖。
 */
export function getInboundChannelFromURL(fallback: string = ""): string {
  try {
    const url = new URL(window.location.href);
    return url.searchParams.get("from") || fallback;
  } catch {
    return fallback;
  }
}

/**
 * 在已有 URL 上注入 ?from=<channel>，兼容已有 query / hash。
 * 用于分享链接生成时打渠道标签。
 */
export function withInboundChannel(rawUrl: string, channel: string): string {
  if (!rawUrl) return rawUrl;
  try {
    const url = new URL(rawUrl, window.location.origin);
    url.searchParams.set("from", channel);
    return url.toString();
  } catch {
    // rawUrl 非合法 URL（例如 hash 片段）→ 兜底字符串拼接
    const sep = rawUrl.includes("?") ? "&" : "?";
    return `${rawUrl}${sep}from=${encodeURIComponent(channel)}`;
  }
}

/**
 * 数据看板埋点上报。
 *
 * @param post        文章 _id
 * @param viewType    expose | detail | click_origin
 * @param inboundChannel 可选；默认从 URL ?from 取，URL 无 ?from 时不上报 inboundChannel 字段
 * @param options.microColumn 可选；P-2 性能优化，调用方持有完整 post 对象时透传
 * @param options.category    可选；同上
 *
 * 走裸 fetch，不经过 src/utils/request.js（避免 toast / 跳转）。
 * 永远 best-effort：异常吞掉，不返回错误。
 *
 * 2026-05-15 W-2 修正：自然流量不再写 'direct' fallback。
 * - 调用方传 inboundChannel 或 URL 带 ?from=xxx → 写渠道值
 * - 都没有 → 不带 inboundChannel 字段，后端聚合时归 'natural' 桶
 *
 * 2026-05-15 P-2：调用方传 microColumn / category 时 backend 跳过 Post.findById
 * 主表查询（高频 expose 路径每用户每天 30 次 → 节省 60%+ 数据库读压力）。
 */
export async function trackView(
  post: string,
  viewType: ViewType,
  inboundChannel?: string,
  options?: { microColumn?: string; category?: string },
): Promise<void> {
  if (!post) return;
  if (!shouldTrack(post, viewType)) return;

  const channel = inboundChannel || getInboundChannelFromURL();
  // 先打标，避免在 await 期间被同一个组件再次触发（IO 卡片快速进出视口）
  markTracked(post, viewType);

  try {
    const body: Record<string, unknown> = {
      post,
      viewType,
      client: TRACK_CLIENT,
      did: getDid(),
    };
    // W-2：channel 为空字符串视为自然流量，不带 inboundChannel 字段
    if (channel) {
      body.inboundChannel = channel;
    }
    // P-2 / P-3：调用方传 options 时显式声明持有完整 post 元数据，backend 走 fast path
    // 跳过 Post.findById。metaProvided=true 让 backend 信任 body 字段（缺失即真的没有）。
    if (options) {
      body.metaProvided = true;
      if (options.microColumn) body.microColumn = options.microColumn;
      if (options.category) body.category = options.category;
    }
    const res = await fetch(TRACK_API, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(body),
    });
    // 即使 code != 0 也不重试 / 不弹窗。
    // 服务端反爬（2026-06-10 起 1 次/天）也是返回 code:0 的 best-effort 语义。
    if (!res.ok) {
      // 网络层失败，撤销本地标记，下次有机会重发
      try {
        sessionStorage.removeItem(`${TRACK_PREFIX}${post}:${viewType}`);
      } catch {
        // ignore
      }
    }
  } catch {
    // 网络异常：撤销标记，best-effort
    try {
      sessionStorage.removeItem(`${TRACK_PREFIX}${post}:${viewType}`);
    } catch {
      // ignore
    }
  }
}
